"use client";

import { useCallback, useEffect, useState } from "react";
import type { FilterClause } from "../domain";
import { useTableConfig } from "./table-provider";

const MAX_RECENT = 10;

export interface RecentFilterEntry {
  id: string;
  filters: FilterClause[];
  timestamp: number;
}

function loadRecent(key: string): RecentFilterEntry[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e) => e && typeof e.id === "string" && Array.isArray(e.filters),
    );
  } catch {
    return [];
  }
}

function saveRecent(key: string, entries: RecentFilterEntry[]) {
  try {
    localStorage.setItem(key, JSON.stringify(entries));
  } catch {
    // storage full or disabled
  }
}

function filtersKey(filters: FilterClause[]): string {
  return filters
    .map((f) => {
      const parts = [f.column, f.type];
      if ("value" in f) parts.push(String(f.value));
      if ("values" in f) parts.push(f.values.join(","));
      if ("min" in f && f.min !== undefined) parts.push(String(f.min));
      if ("max" in f && f.max !== undefined) parts.push(String(f.max));
      if ("operator" in f && f.operator) parts.push(f.operator);
      if (f.negate) parts.push("NOT");
      return parts.join("|");
    })
    .sort()
    .join(";");
}

export function useRecentFilters() {
  const storageKey = useTableConfig().storageKeys.recentFilters;
  const [recent, setRecent] = useState<RecentFilterEntry[]>([]);

  useEffect(() => {
    setRecent(loadRecent(storageKey));
  }, [storageKey]);

  const addRecent = useCallback(
    (filters: FilterClause[]) => {
      if (filters.length === 0) return;
      setRecent((prev) => {
        const key = filtersKey(filters);
        const filtered = prev.filter((e) => filtersKey(e.filters) !== key);
        const entry: RecentFilterEntry = {
          id: `recent-${Date.now()}`,
          filters,
          timestamp: Date.now(),
        };
        const next = [entry, ...filtered].slice(0, MAX_RECENT);
        saveRecent(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const removeRecent = useCallback(
    (id: string) => {
      setRecent((prev) => {
        const next = prev.filter((e) => e.id !== id);
        saveRecent(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const clearRecent = useCallback(() => {
    setRecent([]);
    saveRecent(storageKey, []);
  }, [storageKey]);

  return { recent, addRecent, removeRecent, clearRecent };
}
